import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useUserContext } from '@/contexts/UserContext';

const AccountPlaybookStatusContext = createContext({});

export function useAccountPlaybookStatus() {
  const context = useContext(AccountPlaybookStatusContext);

  if (!context) {
    console.error('❌ useAccountPlaybookStatus must be used within AccountPlaybookStatusProvider');
    return { statuses: {}, isLoading: false };
  }

  return context;
}

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

let _noUserLoggedOnce = false;

export function AccountPlaybookStatusProvider({ children, accounts = [], currentTenant: propTenant }) {
  const { id: userId, currentTenant: contextTenant } = useUserContext();
  const tenantId = propTenant || contextTenant || 'instructure';
  const [statuses, setStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      setIsLoading(false);
      return;
    }

    if (!userId) {
      if (!_noUserLoggedOnce) {
        _noUserLoggedOnce = true;
        console.warn('AccountPlaybookStatusProvider: No user context, skipping fetch');
      }
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchBatchStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/accounts/batch-playbook-status?tenant_id=${tenantId}&user_id=${encodeURIComponent(userId)}`,
          { credentials: 'include' }
        );

        if (cancelled || !mountedRef.current) return;

        if (!response.ok) throw new Error('Failed to fetch account statuses');

        const statusByName = await response.json();
        const statusMap = {};
        accounts.forEach(account => {
          if (statusByName[account.account_name]) statusMap[account.id] = true;
        });
        setStatuses(statusMap);
      } catch (error) {
        if (!cancelled && mountedRef.current && !_noUserLoggedOnce) {
          _noUserLoggedOnce = true;
          console.warn('AccountPlaybookStatusProvider: Fetch failed', error?.message);
        }
      } finally {
        if (!cancelled && mountedRef.current) setIsLoading(false);
      }
    };

    fetchBatchStatus();
    return () => { cancelled = true; };
  }, [accounts.length, tenantId, userId]);

  return (
    <AccountPlaybookStatusContext.Provider value={{ statuses, isLoading }}>
      {children}
    </AccountPlaybookStatusContext.Provider>
  );
}